<template>
    <div class="reset">
        <p v-if="loadingToken" class="adm-muted reset__checking">Verificando enlace…</p>

        <template v-else-if="!tokenValid">
            <div class="reset__state">
                <span class="reset__icon adm-tone-danger"><LinkIcon :size="26" /></span>
                <h1 class="adm-title">Enlace no válido</h1>
                <p class="adm-muted">El enlace expiró o ya fue utilizado. Solicita uno nuevo.</p>
            </div>
            <AdmButton to="/recover/request" block>Solicitar enlace nuevo</AdmButton>
        </template>

        <template v-else>
            <header class="reset__head">
                <h1 class="adm-title">Hola {{ userName }},</h1>
                <p class="adm-muted">Define tu nueva contraseña.</p>
            </header>

            <form class="reset__form" @submit.prevent="submitNewPassword">
                <AdmField
                    v-model="password"
                    label="Nueva contraseña"
                    type="password"
                    autocomplete="new-password"
                    required
                />

                <AdmField
                    v-model="confirmPassword"
                    label="Repetir contraseña"
                    type="password"
                    autocomplete="new-password"
                    required
                />

                <ul class="reset__rules">
                    <li :class="ruleClass(lengthOk, password.length > 0)">
                        <component :is="lengthOk ? Check : Circle" :size="14" />
                        Mínimo 8 caracteres
                    </li>
                    <li :class="ruleClass(matches, confirmPassword.length > 0)">
                        <component :is="matches ? Check : Circle" :size="14" />
                        Las contraseñas coinciden
                    </li>
                </ul>

                <p v-if="error" class="reset__error adm-tone-danger" role="alert">{{ error }}</p>

                <AdmButton type="submit" :disabled="!canSubmit" :loading="submitting" block>
                    {{ submitting ? 'Actualizando…' : 'Actualizar contraseña' }}
                </AdmButton>
            </form>
        </template>
    </div>
</template>

<script>
/**
 * Definición de nueva contraseña — migrada en la adenda del 27/09/2026.
 *
 * Las reglas de validación (8 caracteres, coincidencia) y la verificación del
 * token no cambian; sólo su presentación y el paso de `alert()` a avisos del
 * panel y a un error dentro del formulario.
 */
import { Check, Circle, Link as LinkIcon } from 'lucide-vue-next';
import { verifyResetToken, resetPassword } from '@/services/authService.js';
import AdmField from '@/components/Admin/ui/AdmField.vue';
import AdmButton from '@/components/Admin/ui/AdmButton.vue';
import { notify } from '@/composables/useAdmToast.js';

export default {
    name: 'RecoverPasswordPage',
    components: { AdmField, AdmButton, LinkIcon },
    data() {
        return {
            loadingToken: true,
            tokenValid: false,
            userName: 'usuario',
            password: '',
            confirmPassword: '',
            submitting: false,
            error: '',
            Check,
            Circle,
        };
    },
    computed: {
        lengthOk() {
            return this.password.length >= 8;
        },
        matches() {
            return this.password.length > 0 && this.password === this.confirmPassword;
        },
        canSubmit() {
            return this.lengthOk && this.matches;
        },
        token() {
            return this.$route.params.token;
        },
    },
    async mounted() {
        const { valid, userName } = await verifyResetToken(this.token);
        this.tokenValid = valid;
        if (valid && userName) {
            this.userName = userName;
        }
        this.loadingToken = false;
    },
    methods: {
        /** Verde al cumplirse; rojo sólo cuando el usuario ya escribió algo. */
        ruleClass(ok, touched) {
            return { 'is-ok': ok, 'is-fail': !ok && touched };
        },
        async submitNewPassword() {
            if (!this.canSubmit) return;
            this.error = '';
            this.submitting = true;
            const { success, error } = await resetPassword(this.token, this.password);
            this.submitting = false;

            if (!success) {
                this.error = error || 'No se pudo actualizar la contraseña.';
                return;
            }
            notify.success('Contraseña actualizada. Inicia sesión con tu nueva clave.');
            this.$router.push('/login');
        },
    },
};
</script>

<style scoped>
.reset {
    display: flex;
    flex-direction: column;
    gap: var(--adm-gap-lg);
}

.reset__checking,
.reset__head { text-align: center; }

.reset__head .adm-muted { margin: 0.35rem 0 0 0; }

.reset__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    text-align: center;
}

.reset__state .adm-muted { margin: 0; }

.reset__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    margin-bottom: 0.25rem;
    border-radius: 50%;
}

.reset__form {
    display: flex;
    flex-direction: column;
    gap: var(--adm-gap);
}

.reset__rules {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    font-family: var(--font-body);
    font-size: var(--adm-font-sm);
    color: var(--color-text);
}

.reset__rules li {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.reset__rules li.is-ok { color: var(--adm-ok-fg); }
.reset__rules li.is-fail { color: var(--adm-danger-fg); }

.reset__error {
    margin: 0;
    padding: 0.6rem 0.75rem;
    border-radius: var(--adm-radius-sm);
    font-size: var(--adm-font-sm);
    line-height: 1.4;
}
</style>
